import { useEffect, useRef, useState } from 'react';

import useEventWithDimensions from 'hooks/useEventWithDimensions';

import { FilterLabels } from 'components/common/GlobalFiltersInPage/globalFiltersInPage.utils';

import {
    GlobalFilterConfigItem,
    GlobalFiltersState,
} from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { Widget } from 'components/Dashboards/Reporting/reporting.types';

interface WidgetGlobalFilters {
    widgetGlobalFiltersConfig: GlobalFilterConfigItem[] | undefined;
    widget?: Widget;
    defaultWidgetNameFromConfigFile: string;
}
export const useWidgetGlobalFilters = ({
    widgetGlobalFiltersConfig,
    widget,
    defaultWidgetNameFromConfigFile,
}: WidgetGlobalFilters) => {
    const { sendEventWithDimensions } = useEventWithDimensions();
    /**
     * Build the initial `globalFilters` state.
     * Takes widgetParams(saved state in backend) and creates globalFilters state
     * with defaults according to widgetGlobalFiltersConfig OR saved state from backend.
     */
    const buildGlobalFiltersState = (
        widgetGlobalFiltersConfig: GlobalFilterConfigItem[] | undefined = [],
        widgetParams?: Record<string, any>,
    ): GlobalFiltersState => {
        return widgetGlobalFiltersConfig.reduce((globalFilter, { key, defaultValues }) => {
            globalFilter[key] = widgetParams?.[key] ?? defaultValues;
            return globalFilter;
        }, {} as GlobalFiltersState);
    };

    const [globalFilters, setGlobalFilters] = useState(
        buildGlobalFiltersState(widgetGlobalFiltersConfig, widget?.params),
    );

    // Watch globalFilters changes and detect which key changed
    const prevGlobalFiltersRef = useRef(globalFilters);

    useEffect(() => {
        const prevFilters = prevGlobalFiltersRef.current;
        const changedKeys: string[] = [];

        // Find keys that changed AND have a value after the change
        for (const key of Object.keys(globalFilters)) {
            const value = globalFilters[key];
            const hasValue = Array.isArray(value) ? value.length > 0 : Boolean(value);

            if (JSON.stringify(prevFilters[key]) !== JSON.stringify(value) && hasValue) {
                changedKeys.push(key);
            }
        }

        if (changedKeys.length > 0) {
            sendEventWithDimensions({
                category: 'Reporting',
                action: 'Component Configured',
                label: `${defaultWidgetNameFromConfigFile} - ${FilterLabels[changedKeys[0]]}`,
            });
        }

        // Update the ref for next comparison
        prevGlobalFiltersRef.current = globalFilters;
    }, [globalFilters, widget]);

    return { globalFilters, setGlobalFilters };
};
